import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, X, Check, AlertTriangle, Info, CheckCircle, XCircle,
  FileText, PenTool, User, Clock, ChevronRight, Trash2, Settings
} from 'lucide-react';

// Notification Types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  category: 'document' | 'signature' | 'client' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, any>;
}

// Notification Context
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

// Notification Provider
export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([
    // Sample notifications
    {
      id: '1',
      type: 'success',
      category: 'signature',
      title: 'Document Signed',
      message: 'John Smith has signed the Warranty Deed',
      timestamp: new Date(Date.now() - 1000 * 60 * 5),
      read: false,
      actionUrl: '/documents/1',
      actionLabel: 'View Document',
    },
    {
      id: '2',
      type: 'info',
      category: 'document',
      title: 'Document Generated',
      message: 'Operating Agreement for ABC Holdings LLC is ready',
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      read: false,
      actionUrl: '/documents/2',
      actionLabel: 'Download',
    },
    {
      id: '3',
      type: 'warning',
      category: 'signature',
      title: 'Signature Reminder',
      message: 'Jane Doe has not yet signed the Quitclaim Deed (5 days remaining)',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      read: true,
      actionUrl: '/documents/3',
      actionLabel: 'Send Reminder',
    },
    {
      id: '4',
      type: 'info',
      category: 'client',
      title: 'New Client Activity',
      message: 'Johnson Family Trust accessed the client portal',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      read: true,
    },
  ]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: String(Date.now()),
      timestamp: new Date(),
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// Notification Bell (for header)
export function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const getIcon = (category: Notification['category']) => {
    switch (category) {
      case 'document': return FileText;
      case 'signature': return PenTool;
      case 'client': return User;
      default: return Bell;
    }
  };

  const getTypeColor = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'text-green-400';
      case 'error': return 'text-red-400';
      case 'warning': return 'text-yellow-400';
      default: return 'text-blue-400';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-primary-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-96 bg-primary-800 border border-primary-600 rounded-xl shadow-2xl z-50 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-primary-700">
                <h3 className="font-semibold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-accent-400 hover:text-accent-300"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {/* Notification List */}
              <div className="max-h-96 overflow-auto">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-primary-400">
                    <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => {
                    const Icon = getIcon(notification.category);
                    return (
                      <div
                        key={notification.id}
                        className={`flex items-start gap-3 p-4 border-b border-primary-700/50 hover:bg-white/5 transition-colors ${
                          !notification.read ? 'bg-accent-500/5' : ''
                        }`}
                      >
                        <div className={`p-2 rounded-lg bg-primary-700/50 ${getTypeColor(notification.type)}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white">{notification.title}</p>
                            {!notification.read && (
                              <span className="w-2 h-2 rounded-full bg-accent-500" />
                            )}
                          </div>
                          <p className="text-sm text-primary-400 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-primary-500">
                              {formatTime(notification.timestamp)}
                            </span>
                            {notification.actionUrl && (
                              <button className="text-xs text-accent-400 hover:text-accent-300">
                                {notification.actionLabel || 'View'}
                              </button>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notification.id);
                          }}
                          className="p-1 text-primary-500 hover:text-white rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-3 bg-primary-900/50 border-t border-primary-700">
                  <button className="w-full text-sm text-primary-400 hover:text-white text-center">
                    View all notifications
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Toast Notifications (for real-time alerts)
interface ToastProps {
  notification: Notification;
  onClose: () => void;
}

export function Toast({ notification, onClose }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      default: return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getBorderColor = () => {
    switch (notification.type) {
      case 'success': return 'border-l-green-500';
      case 'error': return 'border-l-red-500';
      case 'warning': return 'border-l-yellow-500';
      default: return 'border-l-blue-500';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className={`flex items-start gap-3 p-4 bg-primary-800 border border-primary-600 border-l-4 ${getBorderColor()} rounded-lg shadow-xl max-w-sm`}
    >
      {getIcon()}
      <div className="flex-1">
        <p className="font-medium text-white">{notification.title}</p>
        <p className="text-sm text-primary-400 mt-0.5">{notification.message}</p>
      </div>
      <button onClick={onClose} className="text-primary-400 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

// Toast Container
export function ToastContainer() {
  const [toasts, setToasts] = useState<Notification[]>([]);

  // Example: Listen for new notifications and show toasts
  // In production, this would be connected to the notification system

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            notification={toast}
            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

// Hook to show a toast
export function useToast() {
  const { addNotification } = useNotifications();
  
  return {
    success: (title: string, message: string) => {
      addNotification({ type: 'success', category: 'system', title, message });
    },
    error: (title: string, message: string) => {
      addNotification({ type: 'error', category: 'system', title, message });
    },
    warning: (title: string, message: string) => {
      addNotification({ type: 'warning', category: 'system', title, message });
    },
    info: (title: string, message: string) => {
      addNotification({ type: 'info', category: 'system', title, message });
    },
  };
}
